import { useEffect, useState } from "react";
import { MapPin, Clock, ArrowUpRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import { getSchedule } from "@/lib/api";

export default function Schedule() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    getSchedule().then(setData).catch(() => setErr("Couldn't load schedule"));
  }, []);

  return (
    <div data-testid="schedule-page" className="bg-[#121212]">
      <section className="pt-20 md:pt-28 pb-12">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <p className="text-xs uppercase tracking-[0.3em] text-[#C99C3A] mb-5">Truck Schedule</p>
          <h1 className="font-display text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.98] text-[#F5ECD7]">
            Where we'll
            <br />
            <span className="font-serif-italic font-normal text-[#C99C3A]">be next</span>.
          </h1>
        </div>
      </section>

      {err && <p className="text-center text-[#9B2226] py-10">{err}</p>}

      {data && (
        <>
          {/* Today */}
          <section className="px-5 md:px-8 mb-16">
            <Reveal className="max-w-7xl mx-auto bg-[#F5ECD7] text-[#121212] rounded-2xl p-8 md:p-12 grid md:grid-cols-12 gap-8 items-center">
              <div className="md:col-span-7">
                <p className="text-xs uppercase tracking-[0.3em] text-[#9B2226] mb-3">Today</p>
                <h2 className="font-display text-3xl md:text-5xl tracking-tighter leading-tight">{data.today.location}</h2>
                <p className="mt-3 text-[#121212]/70 flex items-center gap-2 text-sm">
                  <MapPin size={16} /> {data.today.address}
                </p>
                <p className="mt-1 text-[#121212]/70 flex items-center gap-2 text-sm">
                  <Clock size={16} /> {data.today.hours}
                </p>
              </div>
              <div className="md:col-span-5 flex md:justify-end">
                <a
                  href={`https://maps.google.com/?q=${encodeURIComponent(data.today.address)}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="today-directions"
                  className="btn-red w-full md:w-auto justify-center"
                >
                  Get Directions <ArrowUpRight size={14} />
                </a>
              </div>
            </Reveal>
          </section>

          {/* Weekly schedule */}
          <section className="section-pad pt-0">
            <div className="max-w-7xl mx-auto px-5 md:px-8 grid md:grid-cols-12 gap-10">
              <Reveal className="md:col-span-5">
                <h2 className="font-display text-3xl md:text-4xl tracking-tighter text-[#F5ECD7]">This week</h2>
                <p className="mt-3 text-[#F5ECD7]/60 max-w-md">Subject to weather. Follow us on Instagram for last-minute changes.</p>
              </Reveal>
              <div className="md:col-span-7 divide-y divide-[#F5ECD7]/10">
                {data.week.map((d, i) => (
                  <Reveal key={d.day} delay={i * 60} className="grid grid-cols-12 gap-3 py-5 items-baseline">
                    <span className="col-span-3 md:col-span-2 font-display text-lg md:text-xl text-[#C99C3A]">{d.day}</span>
                    <span className="col-span-9 md:col-span-7 text-[#F5ECD7] text-sm md:text-base">{d.location}</span>
                    <span className="col-span-12 md:col-span-3 text-[#F5ECD7]/55 text-xs md:text-sm md:text-right">{d.hours}</span>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>

          {/* Upcoming festivals */}
          <section className="section-pad bg-[#0e0e0e]">
            <div className="max-w-7xl mx-auto px-5 md:px-8">
              <Reveal>
                <p className="text-xs uppercase tracking-[0.3em] text-[#C99C3A] mb-4">Upcoming Festivals</p>
                <h2 className="font-display text-4xl md:text-5xl tracking-tighter text-[#F5ECD7]">Catch us at the party.</h2>
              </Reveal>
              <div className="mt-10 grid md:grid-cols-3 gap-5 md:gap-6">
                {data.upcoming_events.map((ev, i) => (
                  <Reveal key={i} delay={i * 100} className="bg-[#1A1A1A] border border-[#F5ECD7]/5 hover:border-[#C99C3A]/40 transition-colors p-7 rounded-2xl">
                    <p className="text-xs uppercase tracking-[0.25em] text-[#C99C3A] mb-2">{ev.date}</p>
                    <h3 className="font-display text-2xl text-[#F5ECD7] tracking-tight">{ev.name}</h3>
                    <p className="mt-2 text-[#F5ECD7]/60 text-sm">{ev.location}</p>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
